import React, { useEffect, useState, Children } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronDown } from 'lucide-react';
interface AboutPageProps {
  onBack: () => void;
}
const workExperience = [{
  company: 'Loblaw Digital',
  role: 'Senior Product Designer',
  period: "April '22 – present",
  location: 'Toronto, ON',
  description: 'Working on the design system team, helping build 100+ components across iOS, Android and Web. Facilitating adoption across the organization.',
  highlights: ['Launching a fully re-designed Checkout experience.', 'Centralizing account management across the enterprise.']
}, {
  company: 'Loblaw Technology',
  role: 'Product Designer',
  period: "June '21 – April '22",
  location: 'Toronto, ON',
  description: "Working on the consent management team, defining and designing the customer experience of consent management across Loblaws' lines of business.",
  highlights: []
}, {
  company: 'MCI Consulting',
  role: 'Product Designer',
  period: "Jan '21 – Feb '22",
  location: 'Remote',
  description: 'Working with start-ups like Goodable, Cinchy and Field Trip Health to design brands and ship websites & apps.',
  highlights: []
}, {
  company: 'Canopy Growth Corporation',
  role: 'UX/UI Designer',
  period: "March '19 – May '21",
  location: 'Ottawa, ON',
  description: 'Led the design strategy for multiple customer facing and internal apps and websites.',
  highlights: ['Collaborated with brand, software engineers, hardware engineers and legal team. All the while taking on the role of Scrum Master within the R&D software team.']
}];
function WorkEntry({
  entry,
  index
}: {
  entry: (typeof workExperience)[0];
  index: number;
}) {
  const [open, setOpen] = useState(false);
  return <motion.div initial={{
    opacity: 0,
    y: 20
  }} whileInView={{
    opacity: 1,
    y: 0
  }} viewport={{
    once: true,
    margin: '-60px'
  }} transition={{
    duration: 0.5,
    ease: 'easeOut',
    delay: index * 0.07
  }} className="border-t border-[#E8E4DF]" data-id="element-152">
      <button onClick={() => setOpen(prev => !prev)} className="w-full py-6 flex items-start justify-between gap-6 text-left group" data-id="element-153">
        <div className="flex flex-col gap-1 md:flex-row md:items-baseline md:gap-8" data-id="element-154">
          <span className="text-base font-bold text-[#1A1A1A] group-hover:text-[#4A4A4A] transition-colors" data-id="element-155">
            {entry.company}
          </span>
          <span className="text-sm text-[#6B6560]" data-id="element-156">{entry.role}</span>
        </div>
        <div className="flex items-center gap-4 shrink-0" data-id="element-157">
          <span className="text-xs text-[#A09A94] font-medium tracking-wide hidden sm:block" data-id="element-158">
            {entry.period}
          </span>
          <motion.span animate={{
          rotate: open ? 180 : 0
        }} transition={{
          duration: 0.25,
          ease: 'easeOut'
        }} className="text-[#A09A94]" data-id="element-159">
            <ChevronDown size={16} data-id="element-160" />
          </motion.span>
        </div>
      </button>

      <AnimatePresence initial={false} data-id="element-161">
        {open && <motion.div initial={{
        height: 0,
        opacity: 0
      }} animate={{
        height: 'auto',
        opacity: 1
      }} exit={{
        height: 0,
        opacity: 0
      }} transition={{
        duration: 0.2,
        ease: [0.4, 0, 1, 1]
      }} className="overflow-hidden" data-id="element-162">
            <div className="pb-8 pl-0 md:pl-0 space-y-5" data-id="element-163">
              <div className="flex items-center gap-3" data-id="element-164">
                <span className="text-xs text-[#A09A94] font-medium tracking-widest uppercase sm:hidden" data-id="element-165">
                  {entry.period}
                </span>
                <span className="text-xs text-[#A09A94] sm:hidden" data-id="element-166">·</span>
                <span className="text-xs text-[#A09A94]" data-id="element-167">{entry.location}</span>
              </div>
              <p className="text-[#6B6560] leading-relaxed text-sm max-w-2xl" data-id="element-168">
                {entry.description}
              </p>
              {entry.highlights.map((h, i) => <p key={i} className="text-sm text-[#6B6560] leading-relaxed max-w-2xl" data-id="element-169">
                  {h}
                </p>)}
            </div>
          </motion.div>}
      </AnimatePresence>
    </motion.div>;
}
export function AboutPage({
  onBack
}: AboutPageProps) {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  const fadeInUp = {
    hidden: {
      opacity: 0,
      y: 30
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.7,
        ease: 'easeOut'
      }
    }
  };
  const stagger = {
    hidden: {
      opacity: 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.12
      }
    }
  };
  return <div className="min-h-screen bg-[#F8F6F3] text-[#6B6560]" data-id="element-170">
      <div className="max-w-3xl mx-auto px-8 md:px-16 py-16 md:py-24 pb-32" data-id="element-171">
        {/* Bio */}
        <motion.section initial="hidden" animate="visible" variants={stagger} className="mb-24 md:mb-32" data-id="element-172">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6" data-id="element-173">
            <motion.h1 variants={fadeInUp} className="text-3xl md:text-4xl font-bold text-[#1A1A1A] leading-snug" data-id="element-174">
              👋 I'm Jean-Philippe, everyone calls me JP
            </motion.h1>

            <motion.div initial={{
            opacity: 0,
            scale: 0.96
          }} animate={{
            opacity: 1,
            scale: 1
          }} transition={{
            duration: 0.8,
            ease: 'easeOut'
          }} className="shrink-0 self-center md:self-auto" data-id="element-175">
              <div className="w-24 h-24 rounded-full bg-[#E8E4DF] overflow-hidden relative" data-id="element-176">
                <img src="https://cdn.magicpatterns.com/uploads/egoQsPDfDB1bZHGekagY85/Gemini_Generated_Image_sz345fsz345fsz34.png" alt="Jean-Philippe portrait" className="w-full h-full object-cover" data-id="element-177" />
                <div className="absolute inset-0 rounded-full img-outline pointer-events-none" data-id="element-178" />
              </div>
            </motion.div>
          </div>

          <motion.div variants={fadeInUp} className="space-y-5 text-base md:text-lg leading-relaxed" data-id="element-179">
            <p data-id="element-180">
              I'm obsessed with systems, interaction design, and the art of
              making the complex feel simple. Currently at Loblaw Digital,
              working on products used by millions of Canadians.
            </p>
            <p data-id="element-181">
              When I'm not hunched over my computer, you'll find me outside with
              family, helping{' '}
              <a href="https://buildcanada.com" target="_blank" rel="noopener noreferrer" className="text-[#1A1A1A] underline underline-offset-2 hover:text-[#4A4A4A] transition-colors" data-id="element-182">
                Build Canada
              </a>{' '}
              or{' '}
              <a href="https://instagram.com/jpdoodling" target="_blank" rel="noopener noreferrer" className="text-[#1A1A1A] underline underline-offset-2 hover:text-[#4A4A4A] transition-colors" data-id="element-183">
                drawing
              </a>
              .
            </p>
          </motion.div>
        </motion.section>

        {/* Skills / Disciplines */}

        {/* Resume — collapsible work experience */}
        <motion.section initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: '-80px'
      }} variants={stagger} data-id="element-184">
          <motion.h2 variants={fadeInUp} className="text-xs font-bold uppercase tracking-widest text-[#A09A94] mb-2" data-id="element-185">
            Experience
          </motion.h2>

          <div className="mt-4" data-id="element-186">
            {workExperience.map((entry, i) => <WorkEntry key={i} entry={entry} index={i} data-id="element-187" />)}
            {/* Final border */}
            <div className="border-t border-[#E8E4DF]" data-id="element-188" />
          </div>
        </motion.section>
      </div>
    </div>;
}